import { QuestionCircleOutlined, WarningFilled } from '@ant-design/icons'
import type { User } from '@lmc/ts-common'
import {
  Button,
  Checkbox,
  Form,
  FormItem,
  Select,
  SelectAddOption,
  theme
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form as AntdForm, Modal, Tooltip } from 'antd'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { DAILY, WEEKLY } from 'screens/Rostering/constants'
import useAllocationTemplateCreate from 'screens/Rostering/hooks/useAllocationTemplateCreate'
import useAllocationTemplateList from 'screens/Rostering/hooks/useAllocationTemplateList'
import useCountChanges from 'screens/Rostering/hooks/useCountChanges'
import useRevisionsPublish, {
  type PublicationType
} from 'screens/Rostering/hooks/useRevisionsPublish'
import type { RotaBuilderFiltersType } from 'screens/Rostering/types'
import {
  getPublishCount,
  getPublishType,
  getWarningsCount
} from 'screens/Rostering/utils'
import { DAY_FIRST_DATE_FORMAT } from 'screens/Rostering/utils.datetime'
import styled from 'styled-components'
import { checkHasFilters } from 'utils'
import DateRange from '../DateRange'

dayjs.extend(isoWeek)

export type PublishModalProps = {
  open: boolean
  onClose: (event: any) => void
  selectedDate: Date
  isDailyView: boolean
  fromDate: string
  toDate: string
  filterParams?: RotaBuilderFiltersType
}

const PublishModal = ({
  open,
  onClose,
  selectedDate,
  isDailyView,
  fromDate,
  toDate,
  filterParams = {}
}: PublishModalProps) => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const [isPublishAllChecked, setIsPublishAllChecked] = useState(false)
  const [excludeDrafts, setExcludeDrafts] = useState(false)
  const { siteShift, ...filters } = filterParams
  const defaultFilters = siteShift ? { siteShift } : undefined

  const weekStart = dayjs(fromDate)
    .startOf('isoWeek')
    .format(DAY_FIRST_DATE_FORMAT)

  const weekEnd = dayjs(fromDate).endOf('isoWeek').format(DAY_FIRST_DATE_FORMAT)

  const hasFilters = useMemo(() => checkHasFilters(filters), [filterParams])

  const flags = useFeatureFlags({
    flagNames: ['rostering_allocation_template', 'rota_published_email']
  })?.data

  const flagsRosteringAllocationTemplate =
    flags?.rostering_allocation_template?.enabled

  const flagsRotaPublishedEmail = flags?.rota_published_email?.enabled

  const [saveAsTemplate, setSaveAsTemplate] = useState(false)
  const [templateValue, setTemplateValue] = useState<string | undefined>(
    undefined
  )

  const { countChangesData, isLoadingCountChanges } = useCountChanges({
    selectedDate,
    intervalType: !isDailyView || isPublishAllChecked ? WEEKLY : DAILY,
    includeWarnings: true,
    enabled: open,
    filterParams: isPublishAllChecked ? defaultFilters : filterParams
  })

  const hasChanges = hasFilters
    ? countChangesData.filteredPublishedChanges > 0
    : countChangesData.changesToPublishedEvents > 0
  const hasDrafts = hasFilters
    ? countChangesData.filteredDrafts + countChangesData.filteredDraftChanges >
      0
    : countChangesData.draftEvents + countChangesData.draftWithChanges > 0

  const type = getPublishType(
    hasDrafts,
    hasChanges,
    excludeDrafts,
    isPublishAllChecked
  )

  const { publishRevisions } = useRevisionsPublish()

  const { templateRotas } = useAllocationTemplateList()

  const { createAllocationTemplate } = useAllocationTemplateCreate()

  const templateAllocationMap = templateRotas.reduce(
    (map: Map<string, string>, templateRota: any) => {
      // eslint-disable-next-line no-underscore-dangle
      map.set(templateRota.templateAlias, templateRota._id)
      return map
    },
    new Map()
  )

  const { missingAllocations, warningStatuses } = useMemo(
    () => getWarningsCount(type, countChangesData?.warnings),
    [type, countChangesData?.warnings]
  )

  const teamMemberOptions = useMemo(
    () =>
      (countChangesData?.creators || []).map(({ id, name }) => ({
        value: id,
        label: name
      })),
    [countChangesData?.creators]
  )

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const titleMap: Record<PublicationType, string> = {
    publish_draft_events: t('PUBLISH_DRAFT'),
    publish_changes: t('PUBLISH_CHANGES'),
    publish_all: t('PUBLISH_ALL')
  }

  const modalTitle = titleMap[type] ?? titleMap.publish_all

  const onPublish = useCallback(
    (event: any) => {
      const formData = form.getFieldsValue()

      if (saveAsTemplate)
        createAllocationTemplate({
          templateAlias: templateValue || '',
          startDate: fromDate,
          rotaId: templateAllocationMap.get(templateValue),
          overwrite: templateAllocationMap.has(templateValue)
        })

      publishRevisions({
        publicationType: type,
        fromDate,
        intervalType:
          formData.publishAllEvents || !isDailyView ? WEEKLY : DAILY,
        createdBy: hasChanges ? formData.teamMember : undefined,
        sendPushNotification: formData.sendPushNotifications,
        sendEmail: formData.sendEmail,
        filterParams: isPublishAllChecked ? undefined : filterParams
      })
      onClose(event)
    },
    [
      isDailyView,
      excludeDrafts,
      fromDate,
      user?.id,
      form,
      filterParams,
      onClose,
      publishRevisions,
      templateValue
    ]
  )

  const publishEventsCount = getPublishCount(type, hasFilters, countChangesData)

  const transformedTemplateOptions = Array.from(
    templateAllocationMap,
    ([templateAlias]) => ({
      label: templateAlias,
      value: templateAlias
    })
  )

  const allocationTemplateSectionVisible =
    flagsRosteringAllocationTemplate &&
    ((!isDailyView && !hasFilters) || (isDailyView && isPublishAllChecked))

  return (
    <Modal
      destroyOnClose
      width={470}
      title={<Title level={1}>{modalTitle}</Title>}
      footer={
        <ModalFooter>
          <Button onClick={onClose}>{t('CANCEL')}</Button>
          <Button
            onClick={onPublish}
            variant="primary"
            disabled={isLoadingCountChanges}
            data-cy="publish-confirm-btn"
          >
            {t('PUBLISH')} ({publishEventsCount})
          </Button>
        </ModalFooter>
      }
      open={open}
      onCancel={onClose}
      data-cy="publish-modal"
    >
      <ModalDivider />
      <DateRange
        fromDate={fromDate}
        toDate={toDate}
        dateFormat={DAY_FIRST_DATE_FORMAT}
      />
      <StyledForm
        form={form}
        layout="vertical"
        initialValues={{
          publishAllEvents: false,
          sendPushNotifications: true,
          sendEmail: false
        }}
      >
        {(isDailyView || hasFilters) && (
          <FormItem name="publishAllEvents" valuePropName="checked">
            <Checkbox
              onChange={(event) => {
                setIsPublishAllChecked(event.target.checked)
              }}
              data-cy="publish-all-events-checkbox"
            >
              <Text>
                {t('PUBLISH_ALL_EVENTS_FROM_{{fromDate}}_TO_{{toDate}}', {
                  fromDate: weekStart,
                  toDate: weekEnd
                })}
              </Text>
            </Checkbox>
          </FormItem>
        )}

        {hasDrafts && hasChanges && (
          <FormItem name="publishOnlyChanges" valuePropName="checked">
            <Checkbox
              onChange={(event) => {
                setExcludeDrafts(event.target.checked)
              }}
              data-cy="exclude-drafts-checkbox"
            >
              <Text>{t('Exclude drafts')}</Text>
            </Checkbox>
          </FormItem>
        )}

        {teamMemberOptions.length >= 2 && (
          <FormItem
            name="teamMember"
            label={<StyledLabel>{t('CHANGES_MADE_BY')}</StyledLabel>}
            short
          >
            <Select
              mode="multiple"
              placeholder="Select team member"
              options={teamMemberOptions}
              data-cy="team-member-select"
            />
          </FormItem>
        )}

        <ModalDivider />

        <PublishDetails>
          <Text data-cy="publish-events-count">
            {t('YOU_WILL_PUBLISH_CHANGES_FOR')}{' '}
            <b>{t('ROTA_EVENTS', { eventCount: publishEventsCount })}</b>.
          </Text>
          <Text data-cy="missing-allocations-count">
            <QuestionIcon />{' '}
            {t('EVENTS_MISSING_ALLOCATION', { eventCount: missingAllocations })}
          </Text>
          <Text data-cy="warning-status-count">
            <WarningIcon />{' '}
            {t('EVENTS_WITH_WARNING_STATUS', {
              eventCount: warningStatuses
            })}
          </Text>
        </PublishDetails>

        <ModalDivider />

        {flagsRotaPublishedEmail && (
          <>
            <FormItem name="sendEmail" valuePropName="checked">
              <Checkbox>
                <Text>{t('SEND_EMAIL_NOTIFICATIONS')}</Text>
              </Checkbox>
            </FormItem>

            <ModalDivider />
          </>
        )}

        {allocationTemplateSectionVisible && (
          <AllocationTemplateSection>
            <FormItem name="saveAsTemplate" valuePropName="checked">
              <Checkbox
                onChange={(event) => {
                  setSaveAsTemplate(event.target.checked)
                }}
                value={saveAsTemplate}
              >
                <Text>{t('SAVE_AS_TEMPLATE')}</Text>
                <Tooltip
                  color={theme.color.neutral.color3}
                  placement="bottom"
                  title={
                    <TooltipContent>
                      {t('SAVE_AS_TEMPLATE_TOOLTIP')}
                    </TooltipContent>
                  }
                  overlayInnerStyle={{ width: '400px', padding: '8px 16px' }}
                >
                  <MoreInfoIcon />
                </Tooltip>
              </Checkbox>
            </FormItem>

            {saveAsTemplate && (
              <FormItem
                label={<StyledLabel>{t('SELECT_OR_ADD_NEW')}</StyledLabel>}
              >
                <SelectAddOption<string>
                  placeholder={t('PLEASE_SELECT')}
                  options={transformedTemplateOptions}
                  inputPlaceholder={t('TYPE_NEW_TEMPLATE_NAME')}
                  selectedValue={templateValue}
                  setSelectedValue={setTemplateValue}
                  data-cy="template-select"
                />
              </FormItem>
            )}
          </AllocationTemplateSection>
        )}

        {/* Hide these until we get them to work */}
        {/* <ModalDivider />
        <FormItem name="sendPushNotifications" valuePropName="checked">
          <Checkbox>
            <Text>{t('SEND_NOTIFICATION_VIA_APP')}</Text>
          </Checkbox>
        </FormItem>*/}
      </StyledForm>
      <ModalDivider />
    </Modal>
  )
}

export default PublishModal

const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  text-transform: none;
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
`

const ModalDivider = styled(Divider)`
  margin: 24px 0px;
`

const StyledForm = styled(Form)`
  gap: 0;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
`

const WarningIcon = styled(WarningFilled)`
  color: ${(props) => props.theme.color.status.warning};
  width: 16px;
  height: 16px;
  margin-right: 4px;
`

const QuestionIcon = styled(QuestionCircleOutlined)`
  color: ${(props) => props.theme.color.status.warning};
  width: 16px;
  height: 16px;
  margin-right: 4px;
`

const PublishDetails = styled.div`
  display: flex;
  flex-direction: column;

  & > *:first-child {
    margin-bottom: 16px;
  }
`

const AllocationTemplateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const MoreInfoIcon = styled(QuestionCircleOutlined)`
  font-size: 16px;
  margin-left: 4px;

  &:hover {
    color: ${(props) => props.theme.color.status.primary};
  }
`

const StyledLabel = styled(Text)`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  margin-bottom: 8px;
`

const TooltipContent = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`
